import { getIllustration } from "@/data/taskCards/illustrations";

const GOLD = "#F5A524";

// Bump this whenever a /taskcards/*.jpg file is replaced so browsers fetch the
// new image instead of a stale cached copy (filenames are reused).
const PHOTO_VERSION = "3";

// Darken a #rrggbb hex by ratio p (0..1) -> rgb() string.
function shade(hex, p) {
  const n = parseInt(hex.slice(1), 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  r = Math.round(r * (1 - p)); g = Math.round(g * (1 - p)); b = Math.round(b * (1 - p));
  return `rgb(${r}, ${g}, ${b})`;
}

// A single 8.5 x 11 in (US Letter) printable cheat sheet, rendered on white.
// Fixed logical size 816 x 1056 px (96 DPI letter) so it maps 1:1 to a PDF
// page and can be scaled down for thumbnails.
export default function TaskSheet({ card, lang, color, setLabel, settingLabels }) {
  const tr = (o) => (o && (o[lang] || o.en)) || "";
  const Illo = getIllustration(card.id);
  const tips = tr(card.tips) || [];
  const header = `linear-gradient(135deg, ${color} 0%, ${shade(color, 0.4)} 100%)`;

  return (
    <div
      data-sheet
      data-testid={`task-sheet-${card.id}`}
      style={{ width: 816, height: 1056, background: "#ffffff", color: "#18181B", position: "relative", overflow: "hidden", fontFamily: "'Outfit', system-ui, sans-serif" }}
    >
      {/* bold colored header banner */}
      <div style={{ background: header, color: "#ffffff", padding: "30px 56px 28px", position: "relative", overflow: "hidden" }}>
        {/* faint oversized motif */}
        <div style={{ position: "absolute", right: -40, top: -50, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,0.12)" }} />
        <div style={{ position: "absolute", right: 60, bottom: -70, width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
        <div style={{ position: "relative" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 28, height: 28, borderRadius: 7, background: "#ffffff", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <CameraGlyph fill={color} />
              </span>
              <span style={{ fontWeight: 700, fontSize: 19, letterSpacing: "-0.01em" }}>DSLR Advisor&trade;</span>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: "#ffffff", background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.5)", borderRadius: 999, padding: "6px 16px" }}>
              {setLabel}
            </span>
          </div>
          <h1 style={{ fontSize: 50, fontWeight: 800, lineHeight: 1.02, margin: 0, letterSpacing: "-0.025em" }}>{tr(card.title)}</h1>
          <p style={{ fontSize: 18, fontWeight: 500, color: "rgba(255,255,255,0.92)", margin: "10px 0 0", maxWidth: 620 }}>{tr(card.tagline)}</p>
        </div>
      </div>

      <div style={{ padding: "26px 56px 0" }}>
        {/* visual: photo or SVG diagram */}
        {card.photo ? (
          <div style={{ borderRadius: 16, overflow: "hidden", border: `3px solid ${color}`, boxShadow: `0 8px 24px ${color}33` }}>
            <img src={`${card.photo}?v=${PHOTO_VERSION}`} alt="" crossOrigin="anonymous" style={{ display: "block", width: "100%", height: 280, objectFit: "cover" }} />
          </div>
        ) : (
          <div style={{ borderRadius: 16, background: `${color}12`, border: `2px solid ${color}33`, padding: "18px 24px" }}>
            <Illo color={color} />
          </div>
        )}

        {/* quick settings */}
        {card.settings && card.settings.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <SectionLabel color={color}>{lang === "es" ? "Ajustes Rápidos" : "Quick Settings"}</SectionLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 12 }}>
              {card.settings.map((s, i) => (
                <span key={i} style={{ fontSize: 16, padding: "9px 15px", borderRadius: 10, background: header, color: "#fff" }}>
                  <strong>{tr(settingLabels[s.k]) || s.k}: </strong>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>{s.v}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* key tips */}
        <div style={{ marginTop: 24 }}>
          <SectionLabel color={color}>{lang === "es" ? "Consejos Clave" : "Key Tips"}</SectionLabel>
          <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0" }}>
            {tips.map((tip, i) => (
              <li key={i} style={{ display: "flex", gap: 12, marginBottom: 11, fontSize: 17, lineHeight: 1.42 }}>
                <span style={{ flexShrink: 0, width: 28, height: 28, borderRadius: 999, background: header, color: "#fff", fontSize: 15, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{i + 1}</span>
                <span style={{ color: "#27272A" }}>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* remember + colored footer pinned to bottom */}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0 }}>
        {card.remember && (
          <div style={{ margin: "0 56px 18px", borderRadius: 14, padding: "16px 20px", background: `${color}16`, borderLeft: `6px solid ${color}`, fontSize: 17 }}>
            <strong style={{ color: shade(color, 0.15) }}>{lang === "es" ? "Recuerda: " : "Remember: "}</strong>
            <span style={{ color: "#27272A" }}>{tr(card.remember)}</span>
          </div>
        )}
        <div style={{ background: header, color: "#fff", padding: "14px 56px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, fontWeight: 600 }}>
          <span style={{ letterSpacing: "0.04em" }}>{lang === "es" ? "★ Hoja de referencia GRATIS" : "★ FREE printable cheat sheet"}</span>
          <span>www.dslradvisor.com</span>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children, color }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color }}>
      <span style={{ width: 22, height: 3, borderRadius: 2, background: color }} />
      {children}
    </span>
  );
}

function CameraGlyph({ fill = "#18181B" }) {
  return (
    <svg width="17" height="17" viewBox="0 0 256 256" fill={fill} aria-hidden>
      <path d="M208 56h-27.7l-13.6-20.4A8 8 0 0 0 160 32H96a8 8 0 0 0-6.7 3.6L75.7 56H48a24 24 0 0 0-24 24v112a24 24 0 0 0 24 24h160a24 24 0 0 0 24-24V80a24 24 0 0 0-24-24Zm-80 128a44 44 0 1 1 44-44 44 44 0 0 1-44 44Z" />
    </svg>
  );
}
